import {useState} from 'react';
import clsx from 'clsx';
import type {ModelAction} from '../../../../../types/responses/ModelAction';
import type {ContractorNotePermissions} from '../types';
import type {PendingActionType} from './CommentActionModal';
import styles from '../PdfRenderer.module.scss';
import {PDFNotesActionsVals} from "../../../../../helpers/mapping/PDFNotesActionsVals";

const {
    ApproveOnStatusChangeGIPApproved,
    ReturnToExpertOnStatusChangeExpertCorrection,
    CancelByGIP,
    SendForApprovalOnStatusChangeOnCheck,
    CancelDecisionGIPInWork,
    Modify,
    Delete,
    ConfirmCompletion,
    NoteNotCompletedConfirmNotCompleted,
    CancelByInitiator,
    CancelCompletion,
    CompleteCompletedGIP,
    AssignToExpert,
} = PDFNotesActionsVals


interface NoteActionsProps {
    noteId: number;
    isOwnNote: boolean;
    noteActions?: ModelAction[];
    // Права там, где бэкенд не отдаёт Actions[] (контрагентский API).
    // Намеренно НЕ синтетические ModelAction.Id: PDFNotesActionsVals зеркалит
    // серверные константы, и выдуманный id означал бы «сервер разрешил» там,
    // где на самом деле «клиент посчитал».
    contractorPermissions?: ContractorNotePermissions;
    onContractorCancelCompletion?: (noteId: number) => Promise<void>;
    // Действия с обязательным комментарием сами ввод не показывают: диалог
    // общий на весь сайдбар (его же открывает кнопка «Выполнить» на карточке).
    onRequestCommentAction: (type: PendingActionType) => void;
    onApproveNote?: (noteId: number) => Promise<void>;
    onCancelNote?: (noteId: number, comment: string) => Promise<void>;
    onReturnToExpert?: (noteId: number, comment: string) => Promise<void>;
    onSendForApproval?: (noteId: number) => Promise<void>;
    onRevokeDecision?: (noteId: number) => Promise<void>;
    onDeleteNote?: (noteId: number) => Promise<void>;
    onEditStart?: () => void;
    onConfirmCompletion?: (noteId: number) => Promise<void>;
    onConfirmNotCompleted?: (noteId: number, comment: string) => Promise<void>;
    onCancelByInitiator?: (noteId: number, comment: string) => Promise<void>;
    onCancelCompletion?: (noteId: number) => Promise<void>;
    onRequestAssignToExpert?: (noteId: number) => void;
}

export function NoteActions({
                                noteId,
                                isOwnNote,
                                noteActions,
                                contractorPermissions,
                                onContractorCancelCompletion,
                                onRequestCommentAction,
                                onApproveNote,
                                onCancelNote,
                                onReturnToExpert,
                                onSendForApproval,
                                onRevokeDecision,
                                onDeleteNote,
                                onEditStart,
                                onConfirmCompletion,
                                onConfirmNotCompleted,
                                onCancelByInitiator,
                                onCancelCompletion,
                                onRequestAssignToExpert,
                            }: NoteActionsProps) {
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    const filteredNoteActions = noteActions?.filter(a => a.ObjectId === noteId) || []

    const hasRight = (right: number): boolean => {
        return filteredNoteActions.some(a => a.Id === right)
    }


    const canApprove = !!onApproveNote && hasRight(ApproveOnStatusChangeGIPApproved);
    const canReturn = !!onReturnToExpert && hasRight(ReturnToExpertOnStatusChangeExpertCorrection);
    const canCancel = !!onCancelNote && hasRight(CancelByGIP);
    const canSendForApproval = !!onSendForApproval && hasRight(SendForApprovalOnStatusChangeOnCheck);
    const canRevokeDecision = !!onRevokeDecision && hasRight(CancelDecisionGIPInWork);
    const canEdit = !noteActions || hasRight(Modify);
    const canDelete = !noteActions || hasRight(Delete);
    const canConfirmCompletion = !!onConfirmCompletion && hasRight(ConfirmCompletion);
    const canConfirmNotCompleted = !!onConfirmNotCompleted && hasRight(NoteNotCompletedConfirmNotCompleted);
    const canCancelByInitiator = !!onCancelByInitiator && hasRight(CancelByInitiator);
    const canCancelCompletion = !!onCancelCompletion && hasRight(CancelCompletion);
    const canCompleteCompletedGIP = hasRight(CompleteCompletedGIP);
    const canAssignToExpert = !!onRequestAssignToExpert && hasRight(AssignToExpert);

    // Контрагентские действия: гейтятся булевыми правами, а не action id.
    // «Выполнить» здесь нет намеренно — эта кнопка живёт на карточке
    // (AnnotationsSidebar, .sidebarCardActions), дублировать её иконкой не нужно.
    const canContractorCancelCompletion = !!onContractorCancelCompletion && !!contractorPermissions?.canCancelCompletion;

    // Компонент возвращает фрагмент, а не свою строку: он встраивается в футер
    // карточки рядом с «Выполнить» и счётчиком комментариев.
    if (isConfirmingDelete) {
        return (
            <>
                <span style={{fontSize: 12, color: 'var(--text-secondary, #475569)', flexShrink: 0}}>
                    Удалить?
                </span>
                <button
                    type="button"
                    className={styles.sidebarEditCancelBtn}
                    onClick={e => {
                        e.stopPropagation();
                        setIsConfirmingDelete(false);
                    }}
                >
                    Отмена
                </button>
                <button
                    type="button"
                    className={styles.sidebarEditSaveBtn}
                    style={{background: 'var(--danger-color, #dc2626)'}}
                    onClick={e => {
                        e.stopPropagation();
                        onDeleteNote?.(noteId);
                    }}
                >
                    Удалить
                </button>
            </>
        );
    }

    return (
        <>
            {canContractorCancelCompletion && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--danger'])}
                    title="Отменить выполнение"
                    onClick={e => {
                        e.stopPropagation();
                        onContractorCancelCompletion!(noteId);
                    }}
                >
                    <i className="fa-solid fa-xmark"/>
                </button>
            )}
            {canSendForApproval && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--primary'])}
                    title="Отправить на согласование"
                    onClick={e => {
                        e.stopPropagation();
                        onSendForApproval!(noteId);
                    }}
                >
                    <i className="fa-solid fa-paper-plane"/>
                </button>
            )}
            {canApprove && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--success'])}
                    title="Утвердить"
                    onClick={e => {
                        e.stopPropagation();
                        onApproveNote!(noteId);
                    }}
                >
                    <i className="fa-solid fa-check"/>
                </button>
            )}
            {canRevokeDecision && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Отменить решение"
                    onClick={e => {
                        e.stopPropagation();
                        onRevokeDecision!(noteId);
                    }}
                >
                    <i className="fa-solid fa-reply-all"></i>
                </button>
            )}
            {canConfirmCompletion && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--success'])}
                    title="Подтвердить выполнение"
                    onClick={e => {
                        e.stopPropagation();
                        onConfirmCompletion!(noteId);
                    }}
                >
                    <i className="fa-solid fa-circle-check"/>
                </button>
            )}
            {canCancelCompletion && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Отменить выполнение"
                    onClick={e => {
                        e.stopPropagation();
                        onCancelCompletion!(noteId);
                    }}
                >
                    <i className="fa-solid fa-rotate-left"/>
                </button>
            )}
            {canConfirmNotCompleted && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--danger'])}
                    title="Замечание не выполнено"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestCommentAction('confirm-not-completed');
                    }}
                >
                    <i className="fa-solid fa-circle-xmark"/>
                </button>
            )}
            {canCancelByInitiator && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--danger'])}
                    title="Отменить"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestCommentAction('cancel-by-initiator');
                    }}
                >
                    <i className="fa-solid fa-ban"/>
                </button>
            )}
            {canReturn && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Вернуть эксперту"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestCommentAction('return');
                    }}
                >
                    <i className="fa-solid fa-rotate-left"/>
                </button>
            )}
            {canCancel && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--danger'])}
                    title="Отменить"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestCommentAction('cancel');
                    }}
                >
                    <i className="fa-solid fa-ban"/>
                </button>
            )}
            {canCompleteCompletedGIP && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Выполнено"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestCommentAction('complete-gip');
                    }}
                >
                    <i className="fa-solid fa-check"/>
                </button>
            )}
            {canAssignToExpert && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Назначить на эксперта"
                    onClick={e => {
                        e.stopPropagation();
                        onRequestAssignToExpert!(noteId);
                    }}
                >
                    <i className="fa-solid fa-arrows-rotate"/>
                </button>
            )}
            {noteId > 0 && onDeleteNote && canDelete && (
                <button
                    type="button"
                    className={clsx(styles.sidebarCardActionBtn, styles['sidebarCardActionBtn--danger'])}
                    title="Удалить замечание"
                    onClick={e => {
                        e.stopPropagation();
                        setIsConfirmingDelete(true);
                    }}
                >
                    <i className="fa-solid fa-trash"/>
                </button>
            )}
            {canEdit && onEditStart && (
                <button
                    type="button"
                    className={styles.sidebarCardActionBtn}
                    title="Редактировать"
                    onClick={e => {
                        e.stopPropagation();
                        onEditStart();
                    }}
                >
                    <i className="fa-solid fa-pen"/>
                </button>
            )}
        </>
    );
}
